<!--
  Верстка адаптирована из шаблона:
  Файл: design-assets/themeforest_4DdWBZd0_education_online_courses_template_istudy (1)/istudy/courses-details.html
  Компонент: accordion-common-style.accordion-transparent (строки 808-1043)
  Стили: design-assets/themeforest_4DdWBZd0_education_online_courses_template_istudy (1)/istudy/assets/css/main.css
  Классы:
    - .accordion-common-style.accordion-transparent (строка 1373)
    - .accordion-button (строка 1523)
-->
<template>
  <div class="accordion" :class="{ 'accordion--faq': variant === 'faq' }">
    <div
      v-for="(item, index) in items"
      :key="index"
      class="accordion-item"
      :class="{ active: item.expanded }"
    >
      <button
        class="accordion-button"
        :class="{ collapsed: !item.expanded }"
        type="button"
        @click="toggleItem(index)"
      >
        <span v-if="variant !== 'faq' && (prefix != null || $slots.prefix)" class="section-prefix">
          <slot name="prefix" :item="item" :index="index">{{ prefix }}</slot>
        </span>
        <span class="accordion-question">
          <slot name="header" :item="item" :index="index">
            {{ item.title }}
          </slot>
        </span>
        <svg
          v-if="variant === 'faq'"
          class="accordion-icon"
          :class="{ rotated: item.expanded }"
          width="25"
          height="25"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M6 9L12 15L18 9" />
        </svg>
        <svg
          v-else
          class="accordion-icon"
          :class="{ rotated: item.expanded }"
          width="13"
          height="7"
          viewBox="0 0 13 7"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M6.42042 7.00358C6.29978 7.00425 6.18019 6.98217 6.0685 6.93863C5.95682 6.89509 5.85524 6.83093 5.76958 6.74983L0.269584 1.49983C0.0969723 1.33506 0 1.11159 0 0.87858C0 0.645566 0.0969723 0.422096 0.269584 0.25733C0.442196 0.0925645 0.676308 0 0.920417 0C1.16453 0 1.39864 0.0925645 1.57125 0.25733L6.42042 4.89483L11.2696 0.26608C11.4449 0.122732 11.6705 0.0478266 11.9012 0.0563326C12.1319 0.0648387 12.3508 0.15613 12.514 0.311963C12.6773 0.467795 12.7729 0.676692 12.7818 0.896908C12.7907 1.11713 12.7123 1.33244 12.5621 1.49983L7.06208 6.74983C6.89135 6.91148 6.6609 7.00261 6.42042 7.00358Z" fill="#252840" />
        </svg>
      </button>
      <Transition name="accordion-content">
        <div v-if="item.expanded" class="accordion-content">
          <slot name="body" :item="item" :index="index" />
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
export interface AccordionItem {
  title?: string
  expanded?: boolean
  /** Текст ответа (для варианта FAQ и др.) */
  answer?: string
  [key: string]: any
}

// Тип для slot props
export type AccordionSlotProps = {
  item: AccordionItem
  index: number
}

interface Props {
  items: AccordionItem[]
  prefix?: string
  /** faq — градиентная кнопка, белый текст, шеврон; default — прозрачный стиль */
  variant?: 'default' | 'faq'
  /** true — только один пункт открыт (как в FaqSection: один activeIndex, клик по открытому закрывает) */
  singleOpen?: boolean
  /** При singleOpen: индекс открытого пункта по умолчанию (0 — первый открыт, -1 — все закрыты) */
  defaultOpenIndex?: number
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  singleOpen: true,
  defaultOpenIndex: 0
})

// Механизм как в старом FaqSection: один activeIndex, клик переключает (открыть этот / закрыть если тот же)
const activeIndex = ref(props.singleOpen ? props.defaultOpenIndex : -1)

// При !singleOpen — своё состояние expanded у каждого пункта (из props или false)
const expandedByIndex = ref<boolean[]>(props.items.map(item => item.expanded ?? false))

// Для отображения: при singleOpen expanded из activeIndex, иначе из expandedByIndex
const items = computed(() =>
  props.items.map((item, i) => ({
    ...item,
    expanded: props.singleOpen ? activeIndex.value === i : expandedByIndex.value[i]
  }))
)

const toggleItem = (index: number) => {
  if (props.singleOpen) {
    activeIndex.value = activeIndex.value === index ? -1 : index
  } else {
    const next = [...expandedByIndex.value]
    next[index] = !next[index]
    expandedByIndex.value = next
  }
}
</script>

<style scoped>
.accordion {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.accordion-item {
  border-radius: 6px;
  background-color: transparent;
  border: 1px solid #e5e7eb;
  margin-bottom: 0;
}

.accordion-button {
  width: 100%;
  height: 65px;
  min-height: 65px;
  padding: 0 16px;
  text-align: left;
  background-color: transparent;
  border: none;
  border-radius: 5px;
  font-family: var(--font-inter);
  font-weight: 600;
  font-size: 16px;
  color: #252840;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  position: relative;
  transition: all 0.2s;
  box-sizing: border-box;
}

.accordion-button:hover {
  background-color: #f8f9fa;
}

.accordion-question {
  flex: 1;
  padding-right: 16px;
}

.accordion-icon {
  flex-shrink: 0;
  transition: transform 0.2s;
}

.accordion-icon.rotated {
  transform: rotate(180deg);
}

.accordion-content {
  padding: 20px 30px 25px;
  font-size: 16px;
  color: #252840;
  background-color: transparent;
  border-top: 1px solid #e5e7eb;
  border-radius: 0 0 6px 6px;
  will-change: transform, opacity;
  backface-visibility: hidden;
}

/* Открытие — плавная анимация; при переключении активная секция скрывается сразу */
.accordion-content-enter-active {
  transition:
    opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.accordion-content-leave-active {
  transition: none;
}

.accordion-content-enter-from,
.accordion-content-leave-to {
  opacity: 0;
  transform: translate3d(0, -8px, 0);
}

.accordion-content-enter-to,
.accordion-content-leave-from {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}

.section-prefix {
  color: #00CE9B;
  font-weight: 600;
}

@media (max-width: 767px) {
  .accordion-button {
    padding: 0 16px;
    min-height: 65px;
    height: 65px;
    font-size: 15px;
  }

  .accordion-content {
    padding: 16px 20px 20px;
  }
}

@media (max-width: 575px) {
  .accordion-button {
    padding: 0 16px;
    min-height: 65px;
    height: 65px;
    font-size: 14px;
  }

  .accordion-content {
    padding: 14px 16px 18px;
  }
}

/* Вариант FAQ: градиентная кнопка, белый текст, анимация градиента при hover */
.accordion--faq {
  gap: 16px;
  /* max-width: 642px; */
}

.accordion--faq .accordion-item {
  background: #fff;
  border: none;
  border-radius: 5px;
  overflow: hidden;
}

.accordion--faq .accordion-button {
  height: auto;
  min-height: auto;
  padding: 18px 24px;
  background-image: linear-gradient(to right, #09AFEA, #19E089, #09AFEA);
  background-size: 200%;
  background-position: left center;
  color: #fff;
  font-size: 20px;
  transition: background-position 0.4s ease-in-out;
}

.accordion--faq .accordion-button:hover {
  background-color: transparent;
  background-position: right center;
}

.accordion--faq .accordion-icon {
  color: #fff;
}

.accordion--faq .accordion-content {
  padding: 20px 24px;
  background: #fff;
  border-top: none;
  color: var(--color-grey);
  font-size: var(--font-size-body);
  line-height: 1.6;
}

@media (max-width: 575px) {
  .accordion--faq .accordion-button {
    padding: 24px 16px;
    font-size: var(--font-size-body);
  }

  .accordion--faq .accordion-content {
    padding: 16px 16px 14px 16px;
  }
}
</style>
